/**
 * Minimal structured logger shared by pi-ai and its consumers. The library
 * never writes files: entries go to an injectable sink (setLogSink). Without
 * a sink, warn/error fall back to console.error and debug/info are dropped.
 * Logging must never throw into the caller.
 */

export type LogLevel = "debug" | "info" | "warn" | "error";

export interface LogEntry {
	ts: string;
	level: LogLevel;
	component: string;
	msg: string;
	[field: string]: unknown;
}

export type LogSink = (entry: LogEntry) => void;

export interface Logger {
	debug(msg: string, fields?: Record<string, unknown>): void;
	info(msg: string, fields?: Record<string, unknown>): void;
	warn(msg: string, fields?: Record<string, unknown>): void;
	error(msg: string, fields?: Record<string, unknown>): void;
}

let sink: LogSink | undefined;

/** Install the process-wide log sink. Pass undefined to restore the default. */
export function setLogSink(next: LogSink | undefined): void {
	sink = next;
}

const REDACTED = "[REDACTED]";
const SENSITIVE_KEY = /(?:^|[_-])(token|api[_-]?key|secret|password|credential|authorization|cookie)(?:$|[_-])/i;
const ENV_ASSIGNMENT =
	/\b[A-Z][A-Z0-9_]*(?:TOKEN|API_KEY|SECRET|PASSWORD|CREDENTIAL|AUTHORIZATION|COOKIE)\s*=\s*[^\s"']+/g;
const AUTHORIZATION_VALUE = /\b(?:bearer|basic)\s+[A-Za-z0-9._~+/=:-]+/gi;
const KNOWN_TOKEN = /\b(?:sk-[A-Za-z0-9_-]+|AIza[A-Za-z0-9_-]+|gh[pousr]_[A-Za-z0-9_-]+)\b/g;

/** Redact common credential formats before diagnostics leave the process. */
export function redactSensitiveText(value: string): string {
	return value
		.replace(ENV_ASSIGNMENT, (assignment) => `${assignment.slice(0, assignment.indexOf("=") + 1)}${REDACTED}`)
		.replace(AUTHORIZATION_VALUE, REDACTED)
		.replace(KNOWN_TOKEN, REDACTED);
}

function jsonReplacer(): (key: string, value: unknown) => unknown {
	const seen = new WeakSet<object>();
	return (key, value) => {
		if (SENSITIVE_KEY.test(key)) return REDACTED;
		if (typeof value === "string") return redactSensitiveText(value);
		if (typeof value === "bigint") return value.toString();
		if (value instanceof Error) {
			return {
				name: value.name,
				message: redactSensitiveText(value.message),
				stack: redactSensitiveText(value.stack ?? ""),
			};
		}
		if (typeof value === "object" && value !== null) {
			if (seen.has(value)) return "[Circular]";
			seen.add(value);
		}
		return value;
	};
}

/** JSON.stringify that never throws and redacts credentials from logs and diagnostics. */
export function stringifyLogEntry(entry: LogEntry): string {
	try {
		return JSON.stringify(entry, jsonReplacer());
	} catch {
		return JSON.stringify({
			ts: entry.ts,
			level: entry.level,
			component: entry.component,
			msg: redactSensitiveText(String(entry.msg)),
			fieldsError: "unserializable fields dropped",
		});
	}
}

function redactLogEntry(entry: LogEntry): LogEntry {
	return JSON.parse(stringifyLogEntry(entry)) as LogEntry;
}

function emit(level: LogLevel, component: string, msg: string, fields?: Record<string, unknown>): void {
	try {
		// Reserved keys win over caller fields so entries can't be misclassified.
		const entry: LogEntry = { ...fields, ts: new Date().toISOString(), level, component, msg };
		try {
			if (sink) {
				sink(redactLogEntry(entry));
				return;
			}
		} catch {
			// Broken sink: fall through to the console fallback.
		}
		if (level === "warn" || level === "error") {
			console.error(stringifyLogEntry(entry));
		}
	} catch {
		// Diagnostics must never break the operation being logged.
	}
}

export function getLogger(component: string): Logger {
	return {
		debug: (msg, fields) => emit("debug", component, msg, fields),
		info: (msg, fields) => emit("info", component, msg, fields),
		warn: (msg, fields) => emit("warn", component, msg, fields),
		error: (msg, fields) => emit("error", component, msg, fields),
	};
}
